import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import SpeechToText from '../SpeechToText';
import TextToSpeech from '../TextToSpeech';

/**
 * TalkMode Component
 * Provides a turn-by-turn audio conversation interface for clients
 * 
 * Features:
 * - Large editable text input with word wrap
 * - Speech-to-text input
 * - Automatic playback of therapist responses
 * - Play/pause/stop controls for ongoing playback
 * - Clear visual feedback for conversation state
 */
const TalkMode = ({
  open,
  onClose,
  onSendMessage,
  isLoading = false,
  autoplayEnabled = true,
  autoplayPaused = false,
  currentlyPlayingMessageId,
  onAutoplayToggle,
  onStopAudio,
  onListeningStart,
  onListeningEnd,
  platformSettings = null,
  assignedTherapistVoiceId = null
}) => {
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Handle speech transcript
  const handleSpeechTranscript = useCallback((transcript) => {
    if (transcript.trim()) {
      setInputText(prevText => {
        const newText = prevText 
          ? `${prevText} ${transcript.trim()}` 
          : transcript.trim();
        return newText;
      });
    }
  }, []);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;
    
    onSendMessage(inputText);
    setInputText('');
  }, [inputText, isLoading, onSendMessage]);


  // Get the appropriate voice for therapist messages
  const getTherapistVoiceId = useCallback(() => {
    // Priority: assigned therapist voice > platform default human therapist voice > general default
    if (assignedTherapistVoiceId) {
      return assignedTherapistVoiceId;
    }
    if (platformSettings?.voiceSettings?.defaultHumanTherapistVoiceId) {
      return platformSettings.voiceSettings.defaultHumanTherapistVoiceId;
    }
    if (platformSettings?.voiceSettings?.defaultAITherapistVoiceId) {
      return platformSettings.voiceSettings.defaultAITherapistVoiceId;
    }
    return 'qS8z9VGvq5TIqGvrjctF'; // Fallback default
  }, [assignedTherapistVoiceId, platformSettings]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        sx: {
          height: '60vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Talk Mode</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Status indicators */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Autoplay: {autoplayEnabled ? (autoplayPaused ? 'PAUSED' : 'ON') : 'OFF'}
          </Typography>
          {currentlyPlayingMessageId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="primary">
                ♪ Playing response...
              </Typography>
              {onStopAudio && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onStopAudio}
                  sx={{ minWidth: 'auto', padding: '2px 8px' }}
                >
                  Stop
                </Button>
              )}
            </Box>
          )}
          {isLoading && (
            <Typography variant="body2" color="text.secondary">
              AI is typing...
            </Typography>
          )}
          {autoplayPaused && (
            <Typography variant="body2" color="warning.main">
              (Another dialog is open)
            </Typography>
          )}
        </Box>

        {/* Main input area */}
        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={isListening ? "Listening... speak now" : "Type or speak your message..."}
            disabled={isLoading}
            sx={{ 
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start'
              }
            }}
          />

          {/* Controls */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Speech to text button with visual feedback */}
            <Paper
              elevation={isListening ? 2 : 0}
              sx={{
                p: 0.5,
                borderRadius: 1,
                bgcolor: isListening ? 'primary.light' : 'transparent',
                transition: 'all 0.3s'
              }}
            >
              <SpeechToText
                onTranscript={handleSpeechTranscript}
                onListeningStart={() => {
                  setIsListening(true);
                  onListeningStart?.();
                }}
                onListeningEnd={() => {
                  setIsListening(false);
                  onListeningEnd?.();
                }}
                disabled={isLoading}
                language="en-US"
                continuous={true}
                interimResults={true}
                showInterimText={true}
              />
            </Paper>


            {/* Send button */}
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !inputText.trim()}
              endIcon={<SendIcon />}
              sx={{ ml: 'auto' }}
            >
              Send
            </Button>
          </Box>
        </Box>

      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={onAutoplayToggle}
          variant="outlined"
          size="small"
        >
          {autoplayEnabled ? 'Disable' : 'Enable'} Autoplay
        </Button>
        <Button onClick={onClose} variant="text">
          Close Talk Mode
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TalkMode;